import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { chatFirebaseService } from "../../services/chatFirebaseService";
import ConversationList from "./components/ConversationList";
import MessagesPanel from "./components/MessagesPanel";

const CONVERSATIONS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 30;

const ChatMessages = () => {
  const { t, lang } = useContext(LocaleContext);

  // ---- Conversations (left panel) ----
  const [conversations, setConversations] = useState([]);
  const [conversationsCursor, setConversationsCursor] = useState(null);
  const [conversationsHasMore, setConversationsHasMore] = useState(true);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsLoadingMore, setConversationsLoadingMore] = useState(false);
  const [conversationsError, setConversationsError] = useState(false);

  const [selectedConversation, setSelectedConversation] = useState(null);

  // ---- Messages (right panel) ----
  const [messages, setMessages] = useState([]);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [messagesHasMore, setMessagesHasMore] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesLoadingOlder, setMessagesLoadingOlder] = useState(false);
  const [messagesError, setMessagesError] = useState(false);

  const messagesContainerRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const isLoadingOlderRef = useRef(false);
  const shouldScrollToBottomRef = useRef(false);
  const activeConversationIdRef = useRef(null);

  // ==============================
  // Load conversations
  // ==============================
  const loadConversations = async (isInitial = true) => {
    try {
      if (isInitial) {
        setConversationsLoading(true);
        setConversationsError(false);
      } else {
        setConversationsLoadingMore(true);
      }

      const res = await chatFirebaseService.getConversationsAsync({
        pageSize: CONVERSATIONS_PAGE_SIZE,
        cursor: isInitial ? undefined : conversationsCursor,
      });

      if (!res.isSuccess) {
        setConversationsError(true);
        return;
      }

      const items = res.value?.conversations ?? [];
      setConversationsHasMore(items.length === CONVERSATIONS_PAGE_SIZE);
      setConversationsCursor(res.value?.cursor ?? null);
      setConversations((prev) => (isInitial ? items : [...prev, ...items]));
    } catch (error) {
      console.log(error);
      setConversationsError(true);
    } finally {
      setConversationsLoading(false);
      setConversationsLoadingMore(false);
    }
  };

  useEffect(() => {
    loadConversations(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==============================
  // Select a conversation
  // ==============================
  const handleSelectConversation = (conv) => {
    if (conv.conversationId === selectedConversation?.conversationId) return;

    activeConversationIdRef.current = conv.conversationId;
    setSelectedConversation(conv);
    setMessages([]);
    setMessagesCursor(null);
    setMessagesHasMore(true);
    setMessagesError(false);

    loadMessages(conv.conversationId, null, false);
  };

  // ==============================
  // Load messages (first page or older page)
  // ==============================
  const loadMessages = async (conversationId, cursor, appendOlder) => {
    try {
      if (appendOlder) {
        setMessagesLoadingOlder(true);
      } else {
        setMessagesLoading(true);
        setMessagesError(false);
      }

      const res = await chatFirebaseService.getMessagesAsync(conversationId, {
        pageSize: MESSAGES_PAGE_SIZE,
        cursor,
      });

      // Ignore stale responses if the user already switched conversations
      if (conversationId !== activeConversationIdRef.current) return;

      if (!res.isSuccess) {
        setMessagesError(true);
        return;
      }

      const raw = res.value?.messages ?? [];
      setMessagesHasMore(raw.length === MESSAGES_PAGE_SIZE);
      setMessagesCursor(res.value?.cursor ?? null);

      // API returns newest-first; reverse to oldest-first for rendering
      const ordered = [...raw].reverse();

      if (appendOlder) {
        const container = messagesContainerRef.current;
        if (container) {
          previousScrollHeightRef.current = container.scrollHeight;
          isLoadingOlderRef.current = true;
        }
        setMessages((prev) => [...ordered, ...prev]);
      } else {
        setMessages(ordered);
        shouldScrollToBottomRef.current = true;
      }
    } catch (error) {
      console.log(error);
      setMessagesError(true);
    } finally {
      setMessagesLoading(false);
      setMessagesLoadingOlder(false);
    }
  };

  const handleLoadOlderMessages = () => {
    if (messagesLoadingOlder || !messagesHasMore || !selectedConversation) return;
    loadMessages(selectedConversation.conversationId, messagesCursor, true);
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop <= 20) {
      handleLoadOlderMessages();
    }
  };

  // Restore scroll position after loading older messages / scroll to bottom on open
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (shouldScrollToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
      shouldScrollToBottomRef.current = false;
      return;
    }

    if (isLoadingOlderRef.current) {
      container.scrollTop = container.scrollHeight - previousScrollHeightRef.current;
      isLoadingOlderRef.current = false;
    }
  }, [messages]);

  return (
    <section className="section-chats">
      <div className="default-heading">{t("chats")}</div>

      <div className="my-card">
        <div className="row">
          <div className="col-12 col-md-4">
            <ConversationList
              t={t}
              lang={lang}
              conversations={conversations}
              selectedId={selectedConversation?.conversationId}
              loading={conversationsLoading}
              loadingMore={conversationsLoadingMore}
              error={conversationsError}
              hasMore={conversationsHasMore}
              onSelect={handleSelectConversation}
              onLoadMore={() => loadConversations(false)}
            />
          </div>

          <div className="col-12 col-md-8">
            <MessagesPanel
              t={t}
              lang={lang}
              conversation={selectedConversation}
              messages={messages}
              loading={messagesLoading}
              loadingOlder={messagesLoadingOlder}
              error={messagesError}
              containerRef={messagesContainerRef}
              onScroll={handleMessagesScroll}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatMessages;
